import { CUSTOMER_GENDER_OPTIONS } from '@/lib/api/customers'
import {
  GCC_COUNTRY_CODES,
  PHONE_LOCAL_PLACEHOLDER,
  formatLocalPhoneInput,
} from '@/lib/phone'
import { SALON_DESK_MASTERS } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

/**
 * @param {{
 *   form: Record<string, any>
 *   stylists?: import('@/types/api').Stylist[]
 *   creating?: boolean
 *   onChange: (field: string, value: any) => void
 *   onPhoneCountryChange: (countryCode: string) => void
 *   onPhoneLocalChange: (value: string) => void
 * }} props
 */
export function CustomerFormFields({
  form,
  stylists = [],
  creating = false,
  onChange,
  onPhoneCountryChange,
  onPhoneLocalChange,
}) {
  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="customer-name" className="mb-1.5 block text-sm font-medium">
          Name
        </label>
        <input
          id="customer-name"
          type="text"
          value={form.name}
          onChange={(event) => onChange('name', event.target.value)}
          className={cn(
            'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring',
          )}
          required
        />
      </div>

      <div>
        <label htmlFor="customer-gender" className="mb-1.5 block text-sm font-medium">
          Gender <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <select
          id="customer-gender"
          value={form.gender}
          onChange={(event) => onChange('gender', event.target.value)}
          className={cn(
            'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring',
          )}
        >
          {CUSTOMER_GENDER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">Phone</label>
        <div className="grid gap-2 sm:grid-cols-[140px_minmax(0,1fr)]">
          <select
            value={form.phoneCountry}
            onChange={(event) => onPhoneCountryChange(event.target.value)}
            className={cn(
              'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          >
            {GCC_COUNTRY_CODES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.label} {country.code}
              </option>
            ))}
          </select>
          <input
            id="customer-phone"
            type="tel"
            value={form.phoneLocal}
            onChange={(event) => onPhoneLocalChange(formatLocalPhoneInput(event.target.value, form.phoneCountry))}
            placeholder={PHONE_LOCAL_PLACEHOLDER}
            className={cn(
              'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
            required
          />
        </div>
      </div>

      {SALON_DESK_MASTERS.showCustomerEmail || SALON_DESK_MASTERS.showCustomerDob ? (
        <div className="grid gap-4 md:grid-cols-2">
          {SALON_DESK_MASTERS.showCustomerEmail ? (
            <div>
              <label htmlFor="customer-email" className="mb-1.5 block text-sm font-medium">
                Email
              </label>
              <input
                id="customer-email"
                type="email"
                value={form.email}
                onChange={(event) => onChange('email', event.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
          ) : null}
          {SALON_DESK_MASTERS.showCustomerDob ? (
            <div>
              <label htmlFor="customer-dob" className="mb-1.5 block text-sm font-medium">
                Date of birth
              </label>
              <input
                id="customer-dob"
                type="date"
                value={form.dob}
                onChange={(event) => onChange('dob', event.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {SALON_DESK_MASTERS.showCustomerAllergies ? (
        <div>
          <label htmlFor="customer-allergies" className="mb-1.5 block text-sm font-medium">
            Allergies & sensitivities
          </label>
          <textarea
            id="customer-allergies"
            value={form.allergies}
            onChange={(event) => onChange('allergies', event.target.value)}
            rows={3}
            className={cn(
              'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
            placeholder="e.g. PPD dye, latex, nut oils…"
          />
        </div>
      ) : null}

      {SALON_DESK_MASTERS.showBeautyProfile ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="customer-hair-type" className="mb-1.5 block text-sm font-medium">
              Hair type
            </label>
            <input
              id="customer-hair-type"
              value={form.hair_type}
              onChange={(event) => onChange('hair_type', event.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            />
          </div>
          <div>
            <label htmlFor="customer-hair-colour" className="mb-1.5 block text-sm font-medium">
              Hair colour
            </label>
            <input
              id="customer-hair-colour"
              value={form.hair_colour}
              onChange={(event) => onChange('hair_colour', event.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            />
          </div>
          <div>
            <label htmlFor="customer-skin-type" className="mb-1.5 block text-sm font-medium">
              Skin type
            </label>
            <input
              id="customer-skin-type"
              value={form.skin_type}
              onChange={(event) => onChange('skin_type', event.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="customer-special-instructions" className="mb-1.5 block text-sm font-medium">
              Special instructions
            </label>
            <textarea
              id="customer-special-instructions"
              value={form.special_instructions}
              onChange={(event) => onChange('special_instructions', event.target.value)}
              rows={2}
              className={cn(
                'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            />
          </div>
        </div>
      ) : null}

      {SALON_DESK_MASTERS.showCustomerPreferredStylist ? (
        <div>
          <label htmlFor="customer-stylist" className="mb-1.5 block text-sm font-medium">
            Preferred stylist
          </label>
          <select
            id="customer-stylist"
            value={form.preferred_stylist_id}
            onChange={(event) => onChange('preferred_stylist_id', event.target.value)}
            className={cn(
              'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          >
            <option value="">None</option>
            {stylists.map((stylist) => (
              <option key={stylist.id} value={stylist.id}>
                {stylist.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div>
        <label htmlFor="customer-notes" className="mb-1.5 block text-sm font-medium">
          Notes
        </label>
        <textarea
          id="customer-notes"
          value={form.notes}
          onChange={(event) => onChange('notes', event.target.value)}
          rows={3}
          className={cn(
            'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring',
          )}
        />
      </div>

      {SALON_DESK_MASTERS.showCustomerMarketingConsent ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.marketing_consent}
            onChange={(event) => onChange('marketing_consent', event.target.checked)}
            className="rounded border-border"
          />
          Marketing consent (WhatsApp / SMS / email)
        </label>
      ) : null}

      {!creating && SALON_DESK_MASTERS.showCustomerBlacklist ? (
        <label className="flex items-center gap-2 text-sm text-destructive">
          <input
            type="checkbox"
            checked={form.blacklist}
            onChange={(event) => onChange('blacklist', event.target.checked)}
            className="rounded border-border"
          />
          Blacklisted — block bookings and POS
        </label>
      ) : null}
    </div>
  )
}
